// Share posters must show the booked result, never the planned risk multiple.
// r_value on a setup is the plan (and on spot, a portfolio %). The futures
// book (`analytic` field `r`) is the same number Performance uses.

const MATCH_WINDOW_MS = 2 * 24 * 60 * 60 * 1000

export type Side = 'long' | 'short'

export type SetupLike = {
  id: string
  coin_name: string
  coin_icon?: string
  position?: string | null
  status?: string
  category?: string
  entry_value?: number | string | null
  close_price?: number | string | null
  close_date?: Date | string | null
  stop_value?: number | string | null
  is_tp1?: boolean | null
  is_tp2?: boolean | null
  is_tp3?: boolean | null
  profit_taking_1?: number | null
  profit_taking_2?: number | null
  profit_taking_3?: number | null
}

export type BookRow = {
  coin_name?: string
  position?: string | null
  r?: unknown
  pnl?: unknown
  target_date?: string | null
  close_date?: string | null
}

export type ShareTrade = {
  id: string
  coin: string
  pair: string
  icon: string
  category: string
  position: Side | null
  status: string
  realizedR: number | null
  rSource: 'book' | 'prices' | 'none'
  entry: number | null
  close: number | null
  stop: number | null
  tpHits: boolean[]
  closedAt: string | null
}

export function num(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

export function formatR(value: number): string {
  const rounded = Math.round(value * 100) / 100
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded.toFixed(2)}R`
}

export function formatPrice(value: number): string {
  const abs = Math.abs(value)
  const digits = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8
  return `$${value.toLocaleString('en-US', {maximumFractionDigits: digits})}`
}

export function formatPosterDate(value: string | Date | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
}

function parseTime(value: string | Date | null | undefined): number | null {
  if (!value) return null
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? t : null
}

export function realizedRFromPrices(input: {
  entry: number | null
  stop: number | null
  close: number | null
  position: Side | null
}): number | null {
  const {entry, stop, close} = input
  if (entry == null || stop == null || close == null) return null
  const risk = Math.abs(entry - stop)
  if (risk === 0) return null
  const side: Side = input.position ?? (stop < entry ? 'long' : 'short')
  const raw = side === 'short' ? (entry - close) / risk : (close - entry) / risk
  return Math.round(raw * 100) / 100
}

export function matchBookR(
  setup: {coin_name: string; position?: string | null; close_date?: string | Date | null},
  rows: BookRow[]
): number | null {
  const candidates = rows.filter((row) => {
    if ((row.coin_name || '') !== setup.coin_name) return false
    if (setup.position && row.position && row.position !== setup.position) return false
    return num(row.r != null ? row.r : row.pnl) != null
  })
  if (candidates.length === 0) return null

  const when = parseTime(setup.close_date)
  const dated = candidates.filter((row) => parseTime(row.target_date || row.close_date || null) != null)
  if (when != null && dated.length > 0) {
    let best: {r: number; delta: number} | null = null
    for (const row of dated) {
      const rowTime = parseTime(row.target_date || row.close_date || null) as number
      const delta = Math.abs(when - rowTime)
      if (delta > MATCH_WINDOW_MS) continue
      const r = num(row.r != null ? row.r : row.pnl) as number
      if (!best || delta < best.delta) best = {r, delta}
    }
    return best ? best.r : null
  }

  // No dates to line the trades up. Only trust a single book row for this coin.
  if (candidates.length !== 1) return null
  return num(candidates[0].r != null ? candidates[0].r : candidates[0].pnl)
}

function sideOf(position?: string | null): Side | null {
  if (position === 'long' || position === 'short') return position
  return null
}

export function buildShareTrades(setups: SetupLike[], book: BookRow[]): ShareTrade[] {
  return setups
    .filter((s) => ['success', 'stopped', 'closed'].includes(String(s.status || '').toLowerCase()))
    .map((s) => {
      const tpPrices = [s.profit_taking_1, s.profit_taking_2, s.profit_taking_3]
      const tpFlags = [s.is_tp1, s.is_tp2, s.is_tp3]
      const tpHits = tpPrices
        .map((price, i) => (price != null ? Boolean(tpFlags[i]) : null))
        .filter((hit): hit is boolean => hit !== null)
      const position = sideOf(s.position)
      const entry = num(s.entry_value)
      const stop = num(s.stop_value)
      const close = num(s.close_price)
      const bookR = matchBookR(s, book)
      // A single close price cannot represent a scaled exit. Leave R blank
      // unless the futures book has the blended result.
      const priceR =
        tpHits.length <= 1 ? realizedRFromPrices({entry, stop, close, position}) : null
      const name = s.coin_name || ''
      const coin = name.replace(/USDT$/i, '')
      return {
        id: s.id,
        coin,
        pair: /USDT$/i.test(name) ? `${coin}/USDT` : name,
        icon: s.coin_icon || '',
        category: (s.category || 'futures').toUpperCase(),
        position,
        status: String(s.status || 'closed').toLowerCase(),
        realizedR: bookR ?? priceR,
        rSource: bookR != null ? 'book' : priceR != null ? 'prices' : 'none',
        entry,
        close,
        stop,
        tpHits,
        closedAt: s.close_date ? String(s.close_date) : null,
      }
    })
}
